import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getDashboard, getMe } from "@/lib/core.functions";
import { PageHeader, EmptyState } from "@/components/AppShell";
import { formatRange } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { relOne } from "@/lib/relations";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Overview | Leadership Edge Scheduling" },
      { name: "description", content: "Live snapshot of courses, trainers, upcoming sessions, and pending requests." },
      { property: "og:title", content: "Overview | Leadership Edge Scheduling" },
      { property: "og:description", content: "Live snapshot of courses, trainers, and upcoming sessions." },
    ],
  }),
  component: Dashboard,
});

function Stat({ label, value, tone }: { label: string; value: number | string; tone?: "warn" | "good" | undefined }) {
  return (
    <div className="panel p-5">
      <p className="eyebrow">{label}</p>
      <p
        className={
          tone === "warn"
            ? "mt-2 font-display text-3xl text-destructive"
            : tone === "good"
              ? "mt-2 font-display text-3xl text-success"
              : "mt-2 font-display text-3xl text-foreground"
        }
      >
        {value}
      </p>
    </div>
  );
}

function Dashboard() {
  const fetchMe = useServerFn(getMe);
  const fetchDashboard = useServerFn(getDashboard);
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => fetchMe() });
  const { data, isPending } = useQuery({ queryKey: ["dashboard"], queryFn: () => fetchDashboard() });

  if (!me?.isAdmin && !me?.isTrainer) {
    return (
      <>
        <PageHeader eyebrow="Welcome" title="No role assigned yet" />
        <EmptyState
          title="Waiting on access"
          body="Your account is signed in but has no role. Ask an administrator to add you as an admin or link you to a trainer record."
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={me.isAdmin ? "Admin" : "Trainer"}
        title={`Welcome, ${me.fullName?.split(" ")[0] ?? "there"}`}
        description="Cadence, teaching blocks, and deployments at a glance."
        actions={
          me.isAdmin ? (
            <Button variant="brass" asChild>
              <Link to="/schedule">Open schedule builder</Link>
            </Button>
          ) : (
            <Button variant="soft" asChild>
              <Link to="/my-schedule">My schedule</Link>
            </Button>
          )
        }
      />

      {isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Stat label="Active courses" value={data?.activeCourses ?? 0} />
            <Stat label="Active trainers" value={data?.activeTrainers ?? 0} />
            <Stat label="Enrolled participants" value={data?.totalEnrolled ?? 0} tone="good" />
            <Stat label="Unassigned sessions" value={data?.unassigned ?? 0} tone={data?.unassigned ? "warn" : undefined} />
            <Stat
              label="Coverage requests"
              value={data?.coverageRequests ?? 0}
              tone={data?.coverageRequests ? "warn" : undefined}
            />
            <Stat
              label="Pending deviations"
              value={data?.pendingDeviations ?? 0}
              tone={data?.pendingDeviations ? "warn" : undefined}
            />
          </div>

          <section className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="panel p-6">
              <h2 className="font-display text-xl text-foreground">Timeframes</h2>
              {(data?.periods ?? []).length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">No timeframes yet.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {(data?.periods ?? []).map((p) => (
                    <li key={p.id} className="flex items-center justify-between gap-3 border-b border-border pb-3 last:border-0">
                      <div>
                        <p className="font-medium text-foreground">{p.name}</p>
                        <p className="font-mono text-xs text-muted-foreground">{formatRange(p.start_date, p.end_date)}</p>
                      </div>
                      <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize text-muted-foreground">
                        {p.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="panel p-6">
              <h2 className="font-display text-xl text-foreground">Upcoming sessions</h2>
              {(data?.upcoming ?? []).length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">Nothing scheduled ahead.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {(data?.upcoming ?? []).map((s) => (
                    <li key={s.id} className="flex items-center justify-between gap-3 border-b border-border pb-3 last:border-0">
                      <div>
                        <p className="font-medium text-foreground">{relOne(s.courses)?.name ?? "Course"}</p>
                        <p className="font-mono text-xs text-muted-foreground">{formatRange(s.start_date, s.end_date)}</p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {relOne(s.trainers)?.full_name ?? "Unassigned"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </>
      )}
    </>
  );
}
